"use client";

import Script from "next/script";
import { useEffect, useRef, useState, useSyncExternalStore } from "react";
import {
  getThemePreference,
  subscribeTheme,
  type ThemePreference,
} from "@/lib/theme";

type Turnstile = {
  render: (
    element: HTMLElement,
    options: {
      sitekey: string;
      action: string;
      theme: "auto" | "light" | "dark";
      size: "flexible" | "compact";
      callback: (token: string) => void;
      "expired-callback": () => void;
      "error-callback": () => void;
    },
  ) => string;
  remove: (widget: string) => void;
};

export function ContactTurnstile({
  siteKey,
  attempt,
  onToken,
}: {
  siteKey: string;
  attempt: number;
  onToken: (token: string) => void;
}) {
  const container = useRef<HTMLDivElement>(null);
  const [ready, setReady] = useState(false);
  const [error, setError] = useState(false);
  const [retry, setRetry] = useState(0);
  const preference = useSyncExternalStore<ThemePreference>(
    subscribeTheme,
    getThemePreference,
    () => "dark",
  );

  useEffect(() => {
    const turnstile = (window as Window & { turnstile?: Turnstile }).turnstile;
    if (!ready || !turnstile || !container.current) return;
    const element = container.current;
    let widget: string | undefined;
    let size: "flexible" | "compact" | undefined;
    const render = () => {
      // Turnstile's flexible layout still requires 300px; compact fits narrow phones.
      const nextSize = element.clientWidth < 300 ? "compact" : "flexible";
      if (size === nextSize) return;
      if (widget !== undefined) turnstile.remove(widget);
      onToken("");
      size = nextSize;
      widget = turnstile.render(element, {
        sitekey: siteKey,
        action: "contact",
        theme: preference === "system" ? "auto" : preference,
        size,
        callback: (token) => {
          setError(false);
          onToken(token);
        },
        "expired-callback": () => onToken(""),
        "error-callback": () => {
          setError(true);
          onToken("");
        },
      });
    };
    render();
    const observer = new ResizeObserver(render);
    observer.observe(element);
    return () => {
      observer.disconnect();
      if (widget !== undefined) turnstile.remove(widget);
      onToken("");
    };
  }, [ready, siteKey, attempt, retry, onToken, preference]);

  return (
    <div>
      <Script
        src="https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit"
        onReady={() => setReady(true)}
        onError={() => {
          setError(true);
          onToken("");
        }}
      />
      <div
        ref={container}
        aria-label="Security check"
        className="min-h-[65px]"
      />
      {error && (
        <p role="alert" className="text-sm text-danger mt-2">
          The security check could not load.{" "}
          {ready ? (
            <button
              type="button"
              className="underline focus-visible:outline-2"
              onClick={() => {
                setError(false);
                setRetry((value) => value + 1);
              }}
            >
              Try again
            </button>
          ) : (
            "Please reload this page or email support@buddify.app."
          )}
        </p>
      )}
    </div>
  );
}
